"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useMemo, useCallback, useEffect, useTransition } from "react"
import { usePathname } from "next/navigation"
import {
  LEADER_ACTIVE_STATE,
  MEMBER_ACTIVE_STATE,
  MEMBER_ONLY_ACTIVE_STATE,
  ADMIN_ACTIVE_STATE,
  VISITOR_ACTIVE_STATE,
  AUTH_ROLE_ZEST_ADMIN,
  AUTH_ROLE_ZEST_PROVIDER,
  AUTH_ROLE_ZEST_USER,
} from "../../../../../lib/constants"
import MobileMenuButton from "../MobileMenuButton"
import MenuMobileNotLoggedIn from "../MenuMobileNotLoggedIn"
import MenuAdmin from "../MenuAdmin"
import MenuMemberOnly from "../MenuMemberOnly"
import MenuMemberAndLeader from "../MenuMemberAndLeader"
import DesktopNavBarButton from "../DesktopNavBarButton"
import AdminNavLink from "../../../AdminNavLink"
import SignUpButton from "../SignUpButton"
import PersonalAvatar from "../PersonalAvatar"
import Link from "next/link"
import { useReactiveVar } from "@apollo/client/react"
import { isViewWebsiteAsMemberOrLeaderVar } from "../../../../../graphql/reactiveVars"
import useSignOutClient from "../../../../../lib/auth/useSignOutClient"
import get from "lodash/get"
import listUserOrganizations, {
  type ListUserOrganizationsQueryData,
} from "../../../../../graphql/queries/listUserOrganizations"
import { useQuery } from "@apollo/client/react"
import { handleQueryError } from "../../../../../lib/logging"
import useNavigationRequests from "../../../../../hooks/useNavigationRequests"
import useAccountResource from "../../../../../hooks/useAccountResource"
import useMenuSetupStepsRemaining from "../../../../../hooks/useMenuSetupStepsRemaining"
import { getCurrentUserOrganization } from "../getCurrentUserOrganization"
import {
  ADMIN_PRIMARY_NAV,
  activeAdminNavKey,
  adminNavAriaCurrent,
} from "../../../../../lib/admin/adminNav"

/**
 * Replicates the old MUI Upgrade Button sx: gradient pill with shimmer
 * sweep (::before) and lift-on-hover.
 */
const UPGRADE_BUTTON_CLASSES =
  "relative ml-px inline-flex cursor-pointer items-center justify-center overflow-hidden rounded-[100px] px-5 py-2 font-roboto text-[16px] leading-[1.75] font-semibold tracking-[0.05em] normal-case text-[#092C3C] bg-[linear-gradient(135deg,#ffe0ad_0%,#e6ff63_100%)] shadow-[0_10px_15px_-3px_rgba(0,178,255,0.3),0_4px_6px_-2px_rgba(0,178,255,0.3)] transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:bg-[linear-gradient(135deg,#092c3c_0%,#00b2ff_100%)] hover:text-white hover:shadow-[0_20px_25px_-5px_rgba(0,178,255,0.4),0_10px_10px_-5px_rgba(0,178,255,0.4)] before:absolute before:top-0 before:left-[-100%] before:h-full before:w-full before:bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.3)_50%,transparent_100%)] before:transition-all before:duration-[600ms] before:ease-in-out before:content-[''] hover:before:left-full"

/**
 * Fullscreen pending overlay replacing the MUI Backdrop + CircularProgress
 * Loader shown while logout is in flight.
 */
function LogoutPendingOverlay() {
  return (
    <div className="fixed inset-0 z-[1201] flex items-center justify-center bg-black/50">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#00B2FF] border-t-transparent" />
    </div>
  )
}

/**
 * Central logic hub for TopNavBar button rendering.
 *
 * Button visibility is determined by the auth role hierarchy:
 *   Visitor (not logged in) → Providers, Find a group, Sign up, Log in
 *   ZestLife Admin (zest_admin) → Users, Groups, Orgs, Articles (ADMIN_PRIMARY_NAV);
 *     the account dropdown (MenuAdmin) holds the dashboard and admin tools
 *   Zest User (zest_client) → Find a group, Dashboard
 *   Provider (zest_provider) with sub-roles from current org:
 *     Independent Facilitator (isPersonal) → Find a group, Upgrade*, Dashboard
 *     Org Owner (role=owner) → Find a group, Upgrade*, Dashboard
 *     Org Admin (role=administrator) → Find a group, Upgrade*, Dashboard
 *     Org Facilitator (role=provider/facilitator) → Find a group, Dashboard
 *   * Upgrade only shown when no active subscription.
 *
 * See AGENTS.md "Auth Role Hierarchy" for the full reference table.
 */
function ClientSideMenuButtons({
  isLeaderLandingPage,
  isNoFindAGroupButton,
  mobileSignUpButton,
  mobileLoginButton,
  userId,
  authRole,
  me,
  orgId,
}: {
  startingBackgroundSolid?: boolean
  isLeaderLandingPage?: boolean
  isNoFindAGroupButton?: boolean
  mobileSignUpButton?: boolean
  mobileLoginButton?: boolean
  userId?: number | null
  authRole?: string | null
  me?: unknown
  orgId?: string | number | null
}) {
  const { enabled, resourceScope } = useNavigationRequests()
  // Get user details from me object
  const photoUrl = get(me, "photoUrl")
  const firstName = get(me, "firstName")
  const lastName = get(me, "lastName")

  // Get organization data
  const { data: listUserOrganizationsData, error: listUserOrganizationsQueryError } =
    useQuery<ListUserOrganizationsQueryData>(listUserOrganizations, {
      variables: {
        pUserId: userId,
      },
      skip: !enabled || !userId || authRole !== AUTH_ROLE_ZEST_PROVIDER,
    })

  useEffect(() => {
    if (listUserOrganizationsQueryError) {
      handleQueryError(listUserOrganizationsQueryError)
    }
  }, [listUserOrganizationsQueryError])

  const {
    setupStepsRemaining,
    setupTotalSteps,
    organizationSubscription: existingSubscription,
  } = useMenuSetupStepsRemaining({ userId, orgId })

  const hasActiveSubscription =
    existingSubscription && existingSubscription.status === "active"

  const pathname = usePathname()
  const { handleSignOut } = useSignOutClient()

  const isLoggedIn = useMemo(() => enabled && !!userId, [enabled, userId])

  const userOrganizations = useMemo(
    () =>
      listUserOrganizationsData?.listUserOrganizations?.edges?.map(
        (edge) => edge.node,
      ) || [],
    [listUserOrganizationsData],
  )

  const currentOrg = useMemo(
    () => getCurrentUserOrganization(userOrganizations, orgId, pathname),
    [userOrganizations, orgId, pathname],
  )

  const organizationPageOrgId = useMemo(() => {
    if (
      currentOrg?.isPersonal === false &&
      currentOrg.orgId != null &&
      currentOrg.orgId !== ""
    ) {
      return String(currentOrg.orgId)
    }
    return undefined
  }, [currentOrg])

  // Org-role derived flags for button visibility
  // See AGENTS.md "Auth Role Hierarchy" for details
  const currentOrgRole = currentOrg?.role
  const isPersonalOrg = currentOrg?.isPersonal === true
  const isOrgOwnerOrAdmin =
    currentOrgRole === "owner" || currentOrgRole === "administrator"
  const showUpgradeButton =
    authRole === AUTH_ROLE_ZEST_PROVIDER &&
    !hasActiveSubscription &&
    (isPersonalOrg || isOrgOwnerOrAdmin)

  const getRouteForLeaderOnboarding = useCallback(() => {
    if (isLoggedIn) {
      return "/dashboard"
    }
    return "/signup?redirect_to_leader=true"
  }, [isLoggedIn])

  const viewWebsiteAsMemberOrLeader = useReactiveVar(
    isViewWebsiteAsMemberOrLeaderVar,
  )

  const [isPending, startTransition] = useTransition()

  const handleLogout = useCallback(async () => {
    startTransition(async () => {
      await handleSignOut()
      // Full reload on logout: clears Apollo cache and client session state.
      // eslint-disable-next-line @next/next/no-location-assign-relative-destination
      window.location.href = "/"
    })
  }, [handleSignOut])

  const getUserActiveStateType = useCallback(() => {
    if (isLoggedIn) {
      if (authRole == AUTH_ROLE_ZEST_PROVIDER) {
        if (viewWebsiteAsMemberOrLeader == LEADER_ACTIVE_STATE) {
          return LEADER_ACTIVE_STATE
        } else if (viewWebsiteAsMemberOrLeader == MEMBER_ACTIVE_STATE) {
          return MEMBER_ACTIVE_STATE
        }
        return MEMBER_ACTIVE_STATE
      } else if (authRole == AUTH_ROLE_ZEST_USER) {
        return MEMBER_ONLY_ACTIVE_STATE
      } else if (authRole == AUTH_ROLE_ZEST_ADMIN) {
        // maybe add something to the bar to indicate admin
        return ADMIN_ACTIVE_STATE
      }
    }
    return VISITOR_ACTIVE_STATE
  }, [isLoggedIn, authRole, viewWebsiteAsMemberOrLeader])

  const getHrefAndTextForBecomeAGroupLeaderButton = useCallback(() => {
    // only show "become a group leader" when not on leader landing page

    return isLeaderLandingPage
      ? { href: getRouteForLeaderOnboarding(), text: "Get Started" }
      : { href: "/therapists", text: "Providers" }
  }, [isLeaderLandingPage, getRouteForLeaderOnboarding])

  const renderLoggedOutStateButtons = useMemo(() => {
    const { href, text } = getHrefAndTextForBecomeAGroupLeaderButton()
    return [
      <DesktopNavBarButton href={href} key={"logged-out-button-1"}>
        {text}
      </DesktopNavBarButton>,
      isNoFindAGroupButton ? null : (
        <DesktopNavBarButton href={"/"} key={"logged-out-button-2"}>
          Find a group
        </DesktopNavBarButton>
      ),
      <SignUpButton href={"/signup"} key={"logged-out-button-3"}>
        Sign up
      </SignUpButton>,
      <DesktopNavBarButton href={"/login"} key={"logged-out-button-4"}>
        Log in
      </DesktopNavBarButton>,
    ]
  }, [isNoFindAGroupButton, getHrefAndTextForBecomeAGroupLeaderButton])

  const renderFirstWordButtonForLoggedInUser = useCallback(() => {
    switch (getUserActiveStateType()) {
      case LEADER_ACTIVE_STATE:
      case MEMBER_ACTIVE_STATE:
        return isNoFindAGroupButton ? null : (
          <Link
            href={"/"}
            className={"text-black no-underline"}
            key="desktop-link-find-group"
          >
            <DesktopNavBarButton key={"leader-first-word-button"}>
              Find a group
            </DesktopNavBarButton>
          </Link>
        )
      case MEMBER_ONLY_ACTIVE_STATE:
        // Zest users see Find a group (not Providers)
        return isNoFindAGroupButton ? null : (
          <Link
            href={"/"}
            className={"text-black no-underline"}
            key="member-only-link-find-group-first"
          >
            <DesktopNavBarButton key={"member-only-first-word-button"}>
              Find a group
            </DesktopNavBarButton>
          </Link>
        )
      case ADMIN_ACTIVE_STATE: {
        // Admins see Users, Groups, Orgs, Articles (same as TopNavBarAdmin)
        const activeKey = activeAdminNavKey(pathname)
        const ariaCurrent = adminNavAriaCurrent(pathname, activeKey)
        return (
          <React.Fragment key="admin-nav-buttons">
            {ADMIN_PRIMARY_NAV.map((item) => (
              <AdminNavLink
                key={item.key}
                href={item.href}
                active={item.key === activeKey}
                ariaCurrent={ariaCurrent}
              >
                {item.label}
              </AdminNavLink>
            ))}
          </React.Fragment>
        )
      }
    }
  }, [getUserActiveStateType, isNoFindAGroupButton, pathname])

  const renderSecondWordButtonForLoggedInUser = useCallback(() => {
    switch (getUserActiveStateType()) {
      case LEADER_ACTIVE_STATE:
        // Upgrade only for independent facilitator, org owner,
        // or org admin — not org facilitator.
        // See AGENTS.md "Auth Role Hierarchy".
        if (showUpgradeButton) {
          return (
            <div className="hidden min-[800px]:block">
              <Link
                href="/subscription"
                key="leader-upgrade-link"
                className="no-underline"
              >
                <span
                  className={UPGRADE_BUTTON_CLASSES}
                  key={"leader-upgrade-button"}
                >
                  Upgrade
                </span>
              </Link>
            </div>
          )
        } else {
          break
        }

      case MEMBER_ONLY_ACTIVE_STATE:
        // Zest users don't get a second button (Find a group
        // is already the first button)
        return null
      case ADMIN_ACTIVE_STATE:
        // Admin buttons are rendered in
        // renderFirstWordButtonForLoggedInUser
        return null
    }
  }, [getUserActiveStateType, showUpgradeButton])

  // renders the Dashboard button for non-admin logged-in users
  const renderSecondPrimaryIconButton = useCallback(() => {
    const userActiveStateType = getUserActiveStateType()
    if (userActiveStateType === ADMIN_ACTIVE_STATE) return null
    return (
      <Link
        href={"/dashboard"}
        className={"text-black no-underline"}
        key="Dashboard-button"
      >
        <DesktopNavBarButton key={"member-only-second-word-button"}>
          Dashboard
        </DesktopNavBarButton>
      </Link>
    )
  }, [getUserActiveStateType])

  const { data: notifications } = useAccountResource<{ unreadCount?: number }>(
    isLoggedIn ? "/edge/notifications?offset=0&limit=100" : null,
    resourceScope,
  )
  const { data: messages } = useAccountResource<{ unreadCount?: number }>(
    isLoggedIn ? "/edge/messages/unread-messages" : null,
    resourceScope,
  )
  const notificationCount = notifications?.unreadCount ?? 0
  const unreadMsgCount = messages?.unreadCount ?? 0

  const mobileMenuBadgeCount = useMemo(() => {
    if (!isLoggedIn) {
      return 0
    }
    const activeType = getUserActiveStateType()
    if (
      activeType === MEMBER_ONLY_ACTIVE_STATE ||
      activeType === ADMIN_ACTIVE_STATE
    ) {
      return unreadMsgCount + notificationCount
    }
    return unreadMsgCount
  }, [isLoggedIn, getUserActiveStateType, unreadMsgCount, notificationCount])

  /**
   * Renders the role-based dropdown around the given trigger element
   * (desktop avatar or mobile hamburger). Radix couples trigger + content,
   * so each trigger gets its own dropdown root instead of the old shared
   * anchorEl.
   */
  const renderMenu = useCallback(
    (trigger: React.ReactElement) => {
      // We need to know which logged in state we are in order to know which menu to display
      switch (getUserActiveStateType()) {
        case LEADER_ACTIVE_STATE:
        case MEMBER_ACTIVE_STATE:
          return (
            <MenuMemberAndLeader
              activeStateType={getUserActiveStateType()}
              trigger={trigger}
              handleLogout={handleLogout}
              unreadMsgCount={unreadMsgCount}
              setupStepsRemaining={setupStepsRemaining}
              setupTotalSteps={setupTotalSteps}
              firstName={firstName}
              lastName={lastName}
              userId={userId}
              orgId={orgId}
              organizationPageOrgId={organizationPageOrgId}
            />
          )
        case ADMIN_ACTIVE_STATE:
          return (
            <MenuAdmin
              trigger={trigger}
              handleLogout={handleLogout}
              topBarBreakpoint={700}
              activeKey={activeAdminNavKey(pathname)}
              activeAriaCurrent={adminNavAriaCurrent(
                pathname,
                activeAdminNavKey(pathname),
              )}
            />
          )
        case MEMBER_ONLY_ACTIVE_STATE:
          return (
            <MenuMemberOnly
              trigger={trigger}
              handleLogout={handleLogout}
              unreadMsgCount={unreadMsgCount}
              notificationCount={notificationCount}
              userId={userId}
              orgId={orgId}
            />
          )

        case VISITOR_ACTIVE_STATE:
          return <MenuMobileNotLoggedIn trigger={trigger} />
      }
    },
    [
      handleLogout,
      getUserActiveStateType,
      unreadMsgCount,
      notificationCount,
      setupStepsRemaining,
      setupTotalSteps,
      firstName,
      lastName,
      userId,
      orgId,
      organizationPageOrgId,
      pathname,
    ],
  )

  const renderLoggedInStateButton = useMemo(() => {
    return [
      renderFirstWordButtonForLoggedInUser(),
      <div
        className="font-roboto mt-0 mr-[15px] mb-0 ml-0"
        key="second-button-container"
      >
        {renderSecondWordButtonForLoggedInUser()}
      </div>,
      renderSecondPrimaryIconButton(),
      // Org switcher stays in the dropdown menus (CompactOrgSwitcher in
      // MenuMemberAndLeader and MenuMemberOnly).
      <React.Fragment key="personal-avatar-menu">
        {renderMenu(
          <PersonalAvatar
            photoUrl={photoUrl}
            firstName={firstName}
            lastName={lastName}
          />,
        )}
      </React.Fragment>,
    ]
  }, [
    renderFirstWordButtonForLoggedInUser,
    renderSecondWordButtonForLoggedInUser,
    renderSecondPrimaryIconButton,
    renderMenu,
    photoUrl,
    firstName,
    lastName,
  ])

  return [
    <div
      className={"font-roboto hidden flex-row items-center min-[700px]:flex"}
      key="desktop-menu"
    >
      {getUserActiveStateType() === VISITOR_ACTIVE_STATE
        ? renderLoggedOutStateButtons
        : renderLoggedInStateButton}
    </div>,
    !isLoggedIn && mobileSignUpButton ? (
      <div className={"min-[700px]:hidden"} key="mobile-signup">
        <SignUpButton href={"/signup"}>Sign up</SignUpButton>
      </div>
    ) : null,
    !isLoggedIn && mobileLoginButton ? (
      <div className={"min-[700px]:hidden"} key="mobile-login">
        <DesktopNavBarButton href={"/login"}>Log in</DesktopNavBarButton>
      </div>
    ) : null,
    <div
      className={"font-roboto flex flex-row min-[700px]:hidden"}
      key="mobile-menu"
    >
      {renderMenu(<MobileMenuButton badgeCount={mobileMenuBadgeCount} />)}
    </div>,
    isPending ? <LogoutPendingOverlay key="logout-pending" /> : null,
  ]
}

export default ClientSideMenuButtons
